export const DEFAULT_TRACKING_TIME_ZONE = "UTC+00:00";

const TRACKING_TIME_ZONE_STORAGE_KEY = "saboteur.tracking.timeZone";
const MIN_TRACKING_UTC_OFFSET = -12;
const MAX_TRACKING_UTC_OFFSET = 14;

function formatUtcOffset(offsetHours) {
  const sign = offsetHours < 0 ? "-" : "+";
  return `UTC${sign}${String(Math.abs(offsetHours)).padStart(2, "0")}:00`;
}

function getUtcOffsetHours(timeZone) {
  if (timeZone === "UTC") return 0;
  const match = String(timeZone || "").match(/^UTC([+-])(\d{2}):00$/);
  if (!match) return null;

  const hours = Number(match[2]) * (match[1] === "-" ? -1 : 1);
  if (hours < MIN_TRACKING_UTC_OFFSET || hours > MAX_TRACKING_UTC_OFFSET) return null;
  return hours;
}

function isValidTimeZone(timeZone) {
  return getUtcOffsetHours(timeZone) !== null;
}

function shiftToUtcOffset(value, timeZone) {
  const offsetHours = getUtcOffsetHours(timeZone);
  if (offsetHours === null) return null;
  return new Date(value.getTime() + offsetHours * 60 * 60 * 1000);
}

export function parseSessionTimestamp(value) {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const rawValue = String(value).trim();
  let match = rawValue.match(/^(\d{2})(\d{2})(\d{2}),\s*(\d{2}):(\d{2}):(\d{2})$/);
  if (!match) {
    match = rawValue.match(/^(\d{2})-(\d{2})-(\d{2}),\s*(\d{2}):(\d{2}):(\d{2})$/);
  }
  if (match) {
    const [, month, day, year, hour, minute, second] = match;
    return new Date(Date.UTC(
      2000 + Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute),
      Number(second)
    ));
  }

  const sqlMatch = rawValue.match(
    /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})(?:\.\d+)?$/
  );
  if (sqlMatch) {
    const [, year, month, day, hour, minute, second] = sqlMatch;
    return new Date(Date.UTC(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute),
      Number(second)
    ));
  }

  const parsed = new Date(rawValue);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function formatSessionTimestamp(value, timeZone = DEFAULT_TRACKING_TIME_ZONE) {
  const parsed = parseSessionTimestamp(value);
  if (!parsed) return value || "";
  const safeTimeZone = isValidTimeZone(timeZone) ? timeZone : DEFAULT_TRACKING_TIME_ZONE;
  const shifted = shiftToUtcOffset(parsed, safeTimeZone);

  const formatted = new Intl.DateTimeFormat("en-US", {
    timeZone: "UTC",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).format(shifted);
  return formatted;
}

export function getSessionDateKey(value, timeZone = DEFAULT_TRACKING_TIME_ZONE) {
  const parsed = parseSessionTimestamp(value);
  if (!parsed) return "";
  const safeTimeZone = isValidTimeZone(timeZone) ? timeZone : DEFAULT_TRACKING_TIME_ZONE;
  const shifted = shiftToUtcOffset(parsed, safeTimeZone);
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "UTC",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(shifted);
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${values.year}-${values.month}-${values.day}`;
}

export function shiftDateKey(dateKey, dayOffset) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(dateKey || ""))) return "";
  const date = new Date(`${dateKey}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + dayOffset);
  return date.toISOString().slice(0, 10);
}

export function getTrackingTimeZoneOptions() {
  const negativeOffsets = Array.from(
    { length: Math.abs(MIN_TRACKING_UTC_OFFSET) },
    (_, index) => formatUtcOffset(-(index + 1))
  );
  const positiveOffsets = Array.from(
    { length: MAX_TRACKING_UTC_OFFSET },
    (_, index) => formatUtcOffset(index + 1)
  );
  return [DEFAULT_TRACKING_TIME_ZONE, ...negativeOffsets, ...positiveOffsets];
}

export function loadTrackingTimeZone() {
  try {
    const stored = window.localStorage.getItem(TRACKING_TIME_ZONE_STORAGE_KEY);
    return stored && isValidTimeZone(stored) ? stored : DEFAULT_TRACKING_TIME_ZONE;
  } catch {
    return DEFAULT_TRACKING_TIME_ZONE;
  }
}

export function saveTrackingTimeZone(timeZone) {
  if (!isValidTimeZone(timeZone)) return;
  try {
    window.localStorage.setItem(TRACKING_TIME_ZONE_STORAGE_KEY, timeZone);
  } catch {
    // Storage can be unavailable in private or restricted browser contexts.
  }
}
